/**
 * SPEC-PreScreening-Phase1-UI — WI-6 Prescreen page (tabbed queues).
 *
 * Layout approved by Sarah: tabbed queues (Option B), purple Pre-Screen
 * identity, three stat tiles (Total / Prescreened / Remaining), date picker
 * defaulting to next Sunday. Structural scaffolding mirrors qa-list.tsx; the
 * QA-specific logic is intentionally NOT copied. Wiring-critical decisions
 * (queue parsing, counts, reference pill, next-Sunday default) live in
 * `lib/prescreen-helpers.ts`, locked by `lib/__tests__/prescreen-helpers.test.ts`.
 *
 * Consumes WI-2 `GET /api/qa/prescreening/list?date=`. That endpoint is part of
 * the backend slice (SPEC-PreScreening-Phase1) and may not be live in a clean
 * checkout — the page degrades to empty queues rather than erroring so it stays
 * reachable and the layout is verifiable before the backend lands.
 */

import { useMemo, useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar, Download, ShieldCheck, Loader2 } from "lucide-react";
import { Starling } from "@/lib/starling-brand";
import {
  type PrescreenQueue,
  type PullProgress,
  parseQueueParam,
  buildQueueSearch,
  groupedQueueCounts,
  referencePillState,
  getNextSundayEST,
  buildBulkStreamUrl,
  formatPullLabel,
  buildPullDoneToast,
  parsePullErrorReason,
} from "@/lib/prescreen-helpers";

const V3 = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  sub: "#F3F4F6",
  text: "#111827",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  border: "#E5E7EB",
  accent: "#2563EB",
};

const TH_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: V3.textMuted,
  background: V3.sub,
  padding: "8px 8px",
  borderBottom: "1px solid " + V3.border,
};

const TD_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: V3.text,
  padding: "10px 8px",
};

interface PrescreenRow {
  id: string;
  recipientName?: string | null;
  address?: string | null;
  expectedDzeCode?: string | null;
  pdrVerifiedCount?: number | null;
  ownerName?: string | null;
  prescreenedBy?: string | null;
  prescreenedAt?: string | null;
}

interface PrescreenListResponseShape {
  waiting?: PrescreenRow[];
  inProgress?: PrescreenRow[];
  completed?: PrescreenRow[];
  counts?: { total: number; prescreened: number; remaining: number };
}

const STATUS_PILL: Record<PrescreenQueue, { bg: string; color: string; label: string }> = {
  waiting: { bg: V3.sub, color: V3.text, label: "Waiting" },
  in_progress: { bg: "#DBEAFE", color: V3.accent, label: "In Progress" },
  completed: { bg: Starling.goodCalmBg, color: Starling.goodCalm, label: "Completed" },
};

function initials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";
}

function ExpectedDzCell({ code }: { code?: string | null }) {
  if (!code) return <span style={{ color: V3.textMuted }}>—</span>;
  return <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{code}</span>;
}

function ReferenceCell({ count }: { count?: number | null }) {
  const state = referencePillState(count);
  if (state === "verified") {
    return (
      <span
        className="rounded-md px-2 py-0.5"
        style={{ fontSize: 12, fontWeight: 500, background: Starling.purpleSubtleBg, color: Starling.purple }}
      >
        {count}× verified
      </span>
    );
  }
  return (
    <span
      className="rounded-md px-2 py-0.5"
      style={{ fontSize: 12, fontWeight: 500, background: "#DBEAFE", color: "#1D4ED8" }}
    >
      First-time
    </span>
  );
}

function StatusPill({ queue }: { queue: PrescreenQueue }) {
  const s = STATUS_PILL[queue];
  return (
    <span
      className="rounded-md px-2 py-0.5"
      style={{ fontSize: 12, fontWeight: 500, background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

function StatTile({ label, value, testid }: { label: string; value: number; testid: string }) {
  return (
    <div
      className="rounded-md"
      style={{ background: V3.card, border: `1px solid ${V3.border}`, padding: "14px 16px" }}
      data-testid={testid}
    >
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: V3.textMuted }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: V3.text, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function formatPrescreenedAt(at?: string | null): string {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function QueueTable({
  rows,
  queue,
  date,
}: {
  rows: PrescreenRow[];
  queue: PrescreenQueue;
  date: string;
}) {
  const [, setLocation] = useLocation();
  const detailHref = (id: string) => `/qa/prescreen/${id}?date=${date}`;

  if (rows.length === 0) {
    const copy =
      queue === "waiting"
        ? "No tasks waiting to be prescreened for this date."
        : queue === "in_progress"
          ? "No prescreens in progress."
          : "No completed prescreens for this date.";
    return (
      <div
        className="text-center"
        style={{ padding: "40px 16px", color: V3.textMuted, fontSize: 14 }}
        data-testid={`prescreen-empty-${queue.replace("_", "-")}`}
      >
        {copy}
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 6, overflow: "hidden", border: `1px solid ${V3.border}` }}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead style={TH_STYLE}>Recipient</TableHead>
            <TableHead style={TH_STYLE}>Address</TableHead>
            <TableHead style={TH_STYLE}>Expected DZ</TableHead>
            {queue === "waiting" && <TableHead style={TH_STYLE}>Reference</TableHead>}
            {queue === "in_progress" && <TableHead style={TH_STYLE}>Owner</TableHead>}
            {queue === "completed" && <TableHead style={TH_STYLE}>Prescreened by / at</TableHead>}
            <TableHead style={TH_STYLE}>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              data-testid={`prescreen-row-${row.id}`}
              onClick={() => setLocation(detailHref(row.id))}
              style={{ cursor: "pointer" }}
            >
              <TableCell style={TD_STYLE}>
                <Link
                  href={detailHref(row.id)}
                  data-testid={`prescreen-row-link-${row.id}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: V3.text, textDecoration: "none", fontWeight: 500 }}
                >
                  {row.recipientName || "—"}
                </Link>
              </TableCell>
              <TableCell style={{ ...TD_STYLE, color: V3.textSecondary }}>{row.address || "—"}</TableCell>
              <TableCell style={TD_STYLE}>
                <ExpectedDzCell code={row.expectedDzeCode} />
              </TableCell>
              {queue === "waiting" && (
                <TableCell style={TD_STYLE}>
                  <ReferenceCell count={row.pdrVerifiedCount} />
                </TableCell>
              )}
              {queue === "in_progress" && (
                <TableCell style={TD_STYLE}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-flex items-center justify-center rounded-full"
                      style={{
                        width: 22,
                        height: 22,
                        fontSize: 10,
                        fontWeight: 700,
                        background: Starling.purpleSubtleBg,
                        color: Starling.purple,
                      }}
                    >
                      {initials(row.ownerName)}
                    </span>
                    <span>{row.ownerName || "—"}</span>
                  </span>
                </TableCell>
              )}
              {queue === "completed" && (
                <TableCell style={{ ...TD_STYLE, color: V3.textSecondary }}>
                  {row.prescreenedBy ? (
                    <span>
                      {row.prescreenedBy}
                      {row.prescreenedAt ? ` · ${formatPrescreenedAt(row.prescreenedAt)}` : ""}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
              <TableCell style={TD_STYLE}>
                <StatusPill queue={queue} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function PrescreenList() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();

  const dateFromUrl = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const d = params.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    return null;
  }, [searchString]);

  const selectedDate = dateFromUrl ?? getNextSundayEST();
  const activeQueue = parseQueueParam(searchString);

  const setSelectedDate = (date: string) => {
    setLocation(`/qa/prescreening${buildQueueSearch(activeQueue, date)}`, { replace: true });
  };
  const setActiveQueue = (queue: string) => {
    setLocation(`/qa/prescreening${buildQueueSearch(queue as PrescreenQueue, selectedDate)}`, { replace: true });
  };

  const { data } = useQuery<PrescreenListResponseShape>({
    queryKey: ["/api/qa/prescreening/list", selectedDate],
    queryFn: async () => {
      const res = await fetch(`/api/qa/prescreening/list?date=${selectedDate}`, { credentials: "include" });
      // WI-2 may not be live yet — degrade to empty queues so the page stays
      // reachable and the layout is verifiable before the backend lands.
      if (!res.ok) return { waiting: [], inProgress: [], completed: [] };
      return res.json();
    },
  });

  const waiting = data?.waiting ?? [];
  const inProgress = data?.inProgress ?? [];
  const completed = data?.completed ?? [];
  const counts = groupedQueueCounts(data);

  // WI-P3-3 — bulk QATT_1 pull over an SSE stream. The button opens an
  // EventSource; each `progress` event invalidates the list query so tasks
  // appear in the Waiting tab in waves; `done`/`error` close the stream and
  // toast. All copy/format/url logic lives in lib/prescreen-helpers.ts.
  const { toast } = useToast();
  const [isPulling, setIsPulling] = useState(false);
  const [pullProgress, setPullProgress] = useState<PullProgress | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Close any open stream on unmount so we don't leak an EventSource.
  useEffect(() => () => { eventSourceRef.current?.close(); }, []);

  function handlePullQatt1() {
    if (isPulling) return; // guard double-click
    setIsPulling(true);
    setPullProgress(null);

    const es = new EventSource(buildBulkStreamUrl(selectedDate));
    eventSourceRef.current = es;

    const closeStream = () => {
      es.close();
      eventSourceRef.current = null;
      setIsPulling(false);
      setPullProgress(null);
    };

    es.addEventListener("progress", (e) => {
      const payload = JSON.parse((e as MessageEvent).data) as PullProgress;
      setPullProgress(payload);
      // New tasks land in the queue in real time as each chunk commits.
      queryClient.invalidateQueries({ queryKey: ["/api/qa/prescreening/list", selectedDate] });
    });

    es.addEventListener("done", (e) => {
      const payload = JSON.parse((e as MessageEvent).data);
      closeStream();
      queryClient.invalidateQueries({ queryKey: ["/api/qa/prescreening/list", selectedDate] });
      toast(buildPullDoneToast(payload, selectedDate));
    });

    es.addEventListener("error", (e) => {
      // The server sends an event named `error`; the browser also fires a native
      // EventSource `error` (no parseable data) on a connection drop. Both land here.
      const reason = parsePullErrorReason((e as MessageEvent).data);
      closeStream();
      toast({ title: "Pull failed", description: reason, variant: "destructive" });
    });
  }

  return (
    <div
      className="mx-auto max-w-7xl"
      style={{ background: V3.bg, fontFamily: "'Inter', sans-serif", color: V3.text, minHeight: "100vh" }}
      data-testid="prescreen-list-page"
    >
      <div className="flex flex-col gap-6" style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24 }}>
        <div
          className="sticky top-0"
          style={{ zIndex: 40, background: V3.bg, paddingTop: 16, paddingBottom: 16, marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24 }}
        >
          <div className="flex flex-col gap-4">
            {/* Pre-Screen identity banner — purple left-accent + pill */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div
                className="flex items-center gap-3"
                style={{
                  background: Starling.purpleTintBg,
                  borderLeft: `4px solid ${Starling.purple}`,
                  borderRadius: 6,
                  padding: "12px 16px",
                }}
                data-testid="prescreen-identity-banner"
              >
                <span
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5"
                  style={{ background: Starling.purple, color: "#FFFFFF", fontSize: 12, fontWeight: 600 }}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Pre-Screen
                </span>
                <div className="flex flex-col">
                  <span style={{ fontSize: 16, fontWeight: 600, color: Starling.purple, lineHeight: 1.2 }}>
                    Prescreen Queue
                  </span>
                  <span style={{ fontSize: 12, color: V3.textSecondary, lineHeight: 1.2 }}>
                    Verify expected fields before delivery
                  </span>
                </div>
              </div>

              {/* Toolbar */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                    data-testid="input-date-picker"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handlePullQatt1}
                  disabled={isPulling}
                  data-testid="button-pull-qatt1"
                  title="Bulk fetch of the day's QATT_1 into the prescreen queue"
                  style={{ borderColor: Starling.purpleSubtleBorder, color: Starling.purple }}
                >
                  {isPulling ? (
                    <Loader2 className="h-4 w-4 animate-spin" data-testid="spinner-pull-qatt1" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span className="ml-2" data-testid="label-pull-progress">
                    {formatPullLabel({ isPulling, pullProgress, selectedDate })}
                  </span>
                </Button>
              </div>
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-3 gap-3">
              <StatTile label="Total" value={counts.total} testid="stat-total" />
              <StatTile label="Prescreened" value={counts.prescreened} testid="stat-prescreened" />
              <StatTile label="Remaining" value={counts.remaining} testid="stat-remaining" />
            </div>
          </div>
        </div>

        <Tabs value={activeQueue} onValueChange={setActiveQueue}>
          <TabsList style={{ background: "transparent", borderBottom: `1px solid ${V3.border}`, borderRadius: 0, padding: 0, gap: 8 }}>
            <PrescreenTab queue="waiting" label="Waiting" count={waiting.length} active={activeQueue === "waiting"} />
            <PrescreenTab queue="in_progress" label="In Progress" count={inProgress.length} active={activeQueue === "in_progress"} />
            <PrescreenTab queue="completed" label="Completed" count={completed.length} active={activeQueue === "completed"} />
          </TabsList>

          <TabsContent value="waiting" className="mt-4">
            <QueueTable rows={waiting} queue="waiting" date={selectedDate} />
          </TabsContent>
          <TabsContent value="in_progress" className="mt-4">
            <QueueTable rows={inProgress} queue="in_progress" date={selectedDate} />
          </TabsContent>
          <TabsContent value="completed" className="mt-4">
            <QueueTable rows={completed} queue="completed" date={selectedDate} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PrescreenTab({
  queue,
  label,
  count,
  active,
}: {
  queue: PrescreenQueue;
  label: string;
  count: number;
  active: boolean;
}) {
  const testIdQueue = queue.replace("_", "-");
  return (
    <TabsTrigger
      value={queue}
      data-testid={`prescreen-tab-${testIdQueue}`}
      style={{
        background: "transparent",
        borderRadius: 0,
        borderBottom: active ? `2px solid ${Starling.purple}` : "2px solid transparent",
        color: active ? Starling.purple : V3.textSecondary,
        fontWeight: 600,
        fontSize: 14,
        padding: "8px 12px",
        boxShadow: "none",
      }}
    >
      {label}
      <span
        className="ml-2 rounded-full px-2"
        data-testid={`prescreen-tab-count-${testIdQueue}`}
        style={{
          fontSize: 11,
          fontWeight: 600,
          background: active ? Starling.purpleSubtleBg : V3.sub,
          color: active ? Starling.purple : V3.textMuted,
        }}
      >
        {count}
      </span>
    </TabsTrigger>
  );
}
